import type { FailureDescription } from './types';

// Copied by hand from the deny Reasons in Orsnn-Inc/async irm/keyservice/policy.go at feb494e,
// plus download_document_unknown and revoked_document from 35f957b; its only other constant is
// "allowed". A reason added here fails to compile until it has a message below. A reason the key
// service sends that isn't listed here gets GENERIC_FAILURE.
export type RefusalReason =
  | 'unauthenticated'
  | 'download_not_found'
  | 'not_owner_forwarded_copy'
  | 'download_owner_unknown'
  | 'download_company_unknown'
  | 'download_document_unknown'
  | 'doc_level_invalid'
  | 'purpose_invalid'
  | 'revoked_user'
  | 'revoked_company'
  | 'revoked_sale'
  | 'revoked_download'
  | 'revoked_document'
  | 'download_expired'
  | 'reauth_required'
  | 'launcher_version_too_old'
  | 'format_version_too_old'
  | 'sale_access_missing'
  | 'grant_inactive'
  | 'grant_level_insufficient'
  | 'access_cutoff_unset'
  | 'after_access_cutoff'
  | 'access_end_unset'
  | 'access_ended'
  | 'pii_notice_required'
  | 'not_authorized';

export const PII_NOTICE_REQUIRED = 'pii_notice_required' satisfies RefusalReason;

export const GENERIC_FAILURE: FailureDescription = {
  title: "This document can't be opened",
  body: "We couldn't confirm your access to this document. Try again, or contact the sale's administrator if this keeps happening.",
  next: 'none',
};

export const SIGN_IN_AGAIN: FailureDescription = {
  title: 'Sign in again',
  body: 'For your security, protected documents need a recent sign-in. Sign in again, then reopen the file.',
  next: 'sign-in',
};

const COPY_UNVERIFIABLE: FailureDescription = {
  title: "This copy can't be verified",
  body: "We can't confirm the details of this copy. Download it again from the sale's data room.",
  next: 'none',
};

const COPY_OUT_OF_DATE: FailureDescription = {
  title: 'This file is out of date',
  body: "This copy was made with an older version of our protection. Download a new copy from the sale's data room.",
  next: 'none',
};

const ASK_ADMINISTRATOR = "Contact the sale's administrator if you think this is a mistake.";

const MESSAGE_BY_REASON: Record<RefusalReason, FailureDescription> = {
  unauthenticated: SIGN_IN_AGAIN,
  reauth_required: SIGN_IN_AGAIN,
  download_not_found: {
    title: "We can't find this file",
    body: "There is no record of this copy. Download it again from the sale's data room.",
    next: 'none',
  },
  not_owner_forwarded_copy: {
    title: 'This copy belongs to someone else',
    body: "A protected document opens only for the person who downloaded it. Download your own copy from the sale's data room.",
    next: 'none',
  },
  download_owner_unknown: COPY_UNVERIFIABLE,
  download_company_unknown: COPY_UNVERIFIABLE,
  download_document_unknown: COPY_UNVERIFIABLE,
  doc_level_invalid: COPY_UNVERIFIABLE,
  purpose_invalid: COPY_UNVERIFIABLE,
  revoked_user: {
    title: 'Your access has been removed',
    body: `You can no longer open protected documents. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_company: {
    title: "Your company's access has been removed",
    body: `People at your company can no longer open protected documents. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_sale: {
    title: 'Access to this sale has been withdrawn',
    body: `Documents from this sale can no longer be opened. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_download: {
    title: 'This copy has been withdrawn',
    body: 'This copy can no longer be opened. If you still have access to the sale, download a new copy from its data room.',
    next: 'none',
  },
  revoked_document: {
    title: 'This document has been withdrawn',
    body: `This document can no longer be opened. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  download_expired: {
    title: 'This copy has expired',
    body: "Download a new copy from the sale's data room.",
    next: 'none',
  },
  launcher_version_too_old: COPY_OUT_OF_DATE,
  format_version_too_old: COPY_OUT_OF_DATE,
  sale_access_missing: {
    title: "You don't have access to this sale",
    body: `Your account isn't approved for this sale's documents. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  grant_inactive: {
    title: 'Your access to this sale is not active',
    body: `Your access to this sale's documents is paused or has not started. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  grant_level_insufficient: {
    title: 'This document needs more access',
    body: "Your access to this sale doesn't include this document. Contact the sale's administrator to request it.",
    next: 'none',
  },
  access_cutoff_unset: {
    title: 'The review period is not set',
    body: "The review period for this sale hasn't been set yet, so its documents can't be opened. Contact the sale's administrator.",
    next: 'none',
  },
  after_access_cutoff: {
    title: 'The review period has ended',
    body: "This sale's documents can no longer be opened for review.",
    next: 'none',
  },
  access_end_unset: {
    title: 'Access dates are not set',
    body: "The access period for this sale hasn't been set yet, so its documents can't be opened. Contact the sale's administrator.",
    next: 'none',
  },
  access_ended: {
    title: 'Access to this sale has ended',
    body: "The period for viewing this sale's documents is over.",
    next: 'none',
  },
  pii_notice_required: {
    title: 'Please accept the notice first',
    body: 'This document contains personal information. You need to read and accept the notice about it before the document opens.',
    next: 'retry',
  },
  not_authorized: {
    title: "This document can't stay open",
    body: "We couldn't confirm your access to this document. Reopen the file to try again.",
    next: 'retry',
  },
};

function isRefusalReason(reason: string): reason is RefusalReason {
  return Object.prototype.hasOwnProperty.call(MESSAGE_BY_REASON, reason);
}

export function describeRefusal(reason: string): FailureDescription {
  return isRefusalReason(reason) ? MESSAGE_BY_REASON[reason] : GENERIC_FAILURE;
}

export const LOAN_TABLE_GENERIC_FAILURE: FailureDescription = {
  title: "This loan data can't be opened",
  body: "We couldn't confirm your access to this pool's loan data. Try again, or contact the sale's administrator if this keeps happening.",
  next: 'retry',
};

export const LOAN_TABLE_SIGN_IN_AGAIN: FailureDescription = {
  title: 'Sign in again',
  body: "For your security, protected loan data needs a recent sign-in. Sign in again, then open the pool's loans again.",
  next: 'sign-in',
};

const LOAN_TABLE_VIEW_UNVERIFIABLE: FailureDescription = {
  title: "This view can't be verified",
  body: "We can't confirm the details of this view. Go back to the sale and open the pool's loans again.",
  next: 'retry',
};

const LOAN_TABLE_MESSAGE_BY_REASON: Record<RefusalReason, FailureDescription> = {
  unauthenticated: LOAN_TABLE_SIGN_IN_AGAIN,
  reauth_required: LOAN_TABLE_SIGN_IN_AGAIN,
  download_not_found: {
    title: "We can't find this view",
    body: "There is no record of this view of the pool's loans. Go back to the sale and open them again.",
    next: 'retry',
  },
  not_owner_forwarded_copy: {
    title: 'This view belongs to someone else',
    body: "Loan data opens only for the person who opened it. Open the pool's loans from the sale page.",
    next: 'retry',
  },
  download_owner_unknown: LOAN_TABLE_VIEW_UNVERIFIABLE,
  download_company_unknown: LOAN_TABLE_VIEW_UNVERIFIABLE,
  download_document_unknown: LOAN_TABLE_VIEW_UNVERIFIABLE,
  doc_level_invalid: LOAN_TABLE_VIEW_UNVERIFIABLE,
  purpose_invalid: LOAN_TABLE_VIEW_UNVERIFIABLE,
  launcher_version_too_old: LOAN_TABLE_VIEW_UNVERIFIABLE,
  format_version_too_old: LOAN_TABLE_VIEW_UNVERIFIABLE,
  revoked_user: {
    title: 'Your access has been removed',
    body: `You can no longer view protected loan data. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_company: {
    title: "Your company's access has been removed",
    body: `People at your company can no longer view protected loan data. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_sale: {
    title: 'Access to this sale has been withdrawn',
    body: `This sale's loan data can no longer be viewed. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_download: {
    title: 'This view has been closed',
    body: `Your access to this view of the pool's loans was withdrawn. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  revoked_document: {
    title: "This pool's loan data has been withdrawn",
    body: `It can no longer be viewed. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  download_expired: {
    title: 'This view has expired',
    body: "Open the pool's loans again from the sale page.",
    next: 'retry',
  },
  sale_access_missing: {
    title: "You don't have access to this sale",
    body: `Your account isn't approved for this sale's loan data. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  grant_inactive: {
    title: 'Your access to this sale is not active',
    body: `Your access to this sale's loan data is paused or has not started. ${ASK_ADMINISTRATOR}`,
    next: 'none',
  },
  // A lowered level, L2 to L1 while the table is open: a reopen registers a view at the new level.
  grant_level_insufficient: {
    title: 'Your access level changed',
    body: "Open the pool's loans again to view them at your current level.",
    next: 'retry',
  },
  access_cutoff_unset: {
    title: 'The review period is not set',
    body: "The review period for this sale hasn't been set yet, so its loan data can't be viewed. Contact the sale's administrator.",
    next: 'none',
  },
  after_access_cutoff: {
    title: 'The review period has ended',
    body: "This sale's loan data can no longer be viewed for review.",
    next: 'none',
  },
  access_end_unset: {
    title: 'Access dates are not set',
    body: "The access period for this sale hasn't been set yet, so its loan data can't be viewed. Contact the sale's administrator.",
    next: 'none',
  },
  access_ended: {
    title: 'Access to this sale has ended',
    body: "The period for viewing this sale's loan data is over.",
    next: 'none',
  },
  pii_notice_required: {
    title: 'Please accept the notice first',
    body: 'This loan data contains personal information. You need to read and accept the notice about it before it opens.',
    next: 'retry',
  },
  not_authorized: {
    title: "This loan data can't stay open",
    body: "We couldn't confirm your access to this pool's loan data. Open it again to try again.",
    next: 'retry',
  },
};

export function describeLoanTableRefusal(reason: string): FailureDescription {
  return isRefusalReason(reason)
    ? LOAN_TABLE_MESSAGE_BY_REASON[reason]
    : LOAN_TABLE_GENERIC_FAILURE;
}
